type Severity =
  | "DEFAULT"
  | "DEBUG"
  | "INFO"
  | "NOTICE"
  | "WARNING"
  | "ERROR"
  | "CRITICAL"
  | "ALERT"
  | "EMERGENCY";

const errorSeverities = ["WARNING", "ERROR", "CRITICAL", "ALERT", "EMERGENCY"];
const LOG_CHUNK_SIZE = 256;

interface LogContext {
  traceHeader?: string; // const traceHeader = req.header('X-Cloud-Trace-Context');
  userId?: string;
}

// subset of https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry
// Basic example: https://cloud.google.com/run/docs/logging
// Exception / error details: https://cloud.google.com/error-reporting/docs/formatting-error-messages
interface LogEntry {
  severity: Severity;
  message: string;
  "logging.googleapis.com/trace"?: string;
  "@type"?: string;
  data?: unknown;
  context?: {
    userId?: string;
  };
}

interface ReportableError {
  message: string;
  stack: string;
}

const isIndexableObject = (
  data: unknown
): data is Record<string | number | symbol, unknown> =>
  typeof data === "object" && data instanceof Object;

const isReportableError = (data: unknown): data is ReportableError =>
  isIndexableObject(data) &&
  typeof data["message"] === "string" &&
  typeof data["stack"] === "string";

const getReportableError = (data: unknown): ReportableError | undefined => {
  if (!isIndexableObject(data)) {
    return undefined;
  }
  return isReportableError(data)
    ? data
    : Object.values(data).find(isReportableError);
};

class Logger {
  isProduction: boolean;

  // needed for traces to work
  projectId?: string;

  constructor(isProduction: boolean) {
    this.isProduction = isProduction;
  }

  setProjectId(projectId: string): void {
    this.projectId = projectId;
  }

  error(message: string, data?: unknown, context?: LogContext): void {
    this.logAtSeverity("ERROR", message, data, context);
  }

  warn(message: string, data?: unknown, context?: LogContext): void {
    this.logAtSeverity("WARNING", message, data, context);
  }

  info(message: string, data?: unknown, context?: LogContext): void {
    this.logAtSeverity("INFO", message, data, context);
  }

  debug(message: string, data?: unknown, context?: LogContext): void {
    this.logAtSeverity("DEBUG", message, data, context);
  }

  logEntry(message: string, entry: LogEntry, severity: Severity): void {
    const log = errorSeverities.includes(severity)
      ? console.error
      : console.log;

    if (this.isProduction) {
      log(JSON.stringify(entry));
    } else {
      log(message);
      log(JSON.stringify(entry, null, 2));
    }
  }

  private logAtSeverity(
    severity: Severity,
    message: string,
    data?: unknown,
    context?: LogContext
  ): void {
    // Don't log TEST
    if (process.env.NODE_ENV === "test") {
      return;
    }
    const entry: LogEntry = {
      severity,
      message,
      data,
    };

    const error = getReportableError(data);
    if (error) {
      // make sure that the message gets into Error Reporting
      entry.message = `${entry.message} ${error.message}${"\n"}${error.stack}`;
      entry["@type"] =
        "type.googleapis.com/google.devtools.clouderrorreporting.v1beta1.ReportedErrorEvent";
    }

    if (context?.traceHeader && this.projectId) {
      const [trace] = context.traceHeader.split("/");
      entry[
        "logging.googleapis.com/trace"
      ] = `projects/${this.projectId}/traces/${trace}`;
    }

    if (context?.userId) {
      entry.context = {
        userId: context.userId,
      };
    }

    // Split data into chunks to prevent log size overflow
    if (Array.isArray(entry.data) && entry.data.length > LOG_CHUNK_SIZE) {
      const entries = entry.data;
      entries.map((_e, i) => {
        if (i % LOG_CHUNK_SIZE === 0) {
          const chunk = entries.slice(i, i + LOG_CHUNK_SIZE);
          this.logEntry(message, { ...entry, data: chunk }, severity);
        }
      });
    } else {
      this.logEntry(message, entry, severity);
    }
  }
}

export const logger = new Logger(process.env.NODE_ENV === "production");
